import { useIntl } from 'react-intl';
import { useMemo } from 'react';

/**
 * Where [T] is a type of string, e.g. on the form
 * type Keys = 'a' | 'b' | 'c'; and [V] is the type of the values
 * */
type TranslationKeysWithValues<T extends string, V extends unknown> = {
  [key in T]: V;
};

type FormattedTranslations<T extends string> = TranslationKeysWithValues<
  T,
  string
>;

type TranslationsById<T extends string> = {
  [id in T]: { nb: string; en: string };
};

export interface Translation {
  id: string;
  nb: string;
  en: string;
  /** Does not allow injecting of Element etc. */
  values?: { [key: string]: string };
}

export type TranslationObject = { [key: string]: Translation };

/**
 * Where [T] is a type of string, e.g. on the form
 * type Keys = 'a' | 'b' | 'c';
 * */
export type TranslationObjectWithKeys<T extends string> =
  TranslationKeysWithValues<T, Translation>;

/**
 * Where [T] is a type of string, e.g. on the form
 * type Keys = 'a' | 'b' | 'c';
 * */
export const generateTranslations = <T extends string>(
  namespace: string,
  translationsById: TranslationsById<T>,
): TranslationObjectWithKeys<T> => {
  const translations = Object.keys(translationsById).reduce(
    (acc: Partial<TranslationObjectWithKeys<T>>, key) => {
      const id = key as T;

      acc[id] = {
        id: `${namespace}.${id}`,
        ...translationsById[id],
      };

      return acc;
    },
    {},
  );

  return translations as TranslationObjectWithKeys<T>;
};

/**
 * Where [T] is a type of string, e.g. on the form
 * type Keys = 'a' | 'b' | 'c';
 * */
export const useFormatMessageFor = <T extends string>(
  translations: TranslationObjectWithKeys<T>,
): FormattedTranslations<T> => {
  const { formatMessage } = useIntl();

  return useMemo(
    () =>
      Object.entries(translations).reduce(
        (acc: Partial<FormattedTranslations<T>>, [key, value]) => {
          const id = key as T;
          const translation = value as Translation;

          acc[id] = formatMessage({ id: translation.id }, translation.values);

          return acc;
        },
        {},
      ) as FormattedTranslations<T>,
    [formatMessage, translations],
  );
};
